const KEY_PREFIX = "after:task-optimism:";
const TTL_MS = 12 * 60 * 1000;

type TaskOptimismRecord = { at: number };

function storageKey(caseId: string, taskName: string): string {
  return `${KEY_PREFIX}${caseId}:${taskName}`;
}

function readRecord(key: string): TaskOptimismRecord | null {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return typeof parsed?.at === "number"
      ? (parsed as TaskOptimismRecord)
      : null;
  } catch {
    return null;
  }
}

function removeRecord(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Safari private mode etc. — silent no-op
  }
}

export function setTaskOptimism(caseId: string, taskName: string): void {
  try {
    const record: TaskOptimismRecord = { at: Date.now() };
    sessionStorage.setItem(
      storageKey(caseId, taskName),
      JSON.stringify(record),
    );
  } catch {
    // Safari private mode etc. — silent no-op
  }
}

/**
 * Milliseconds since the latch was set, or null when there is no active
 * (unexpired) latch. Expired latches are deleted as a side effect.
 */
export function getTaskOptimismAgeMs(
  caseId: string,
  taskName: string,
): number | null {
  const key = storageKey(caseId, taskName);
  const record = readRecord(key);
  if (!record) return null;

  const age = Date.now() - record.at;
  if (age > TTL_MS) {
    removeRecord(key);
    return null;
  }
  return age;
}

export function hasTaskOptimism(caseId: string, taskName: string): boolean {
  return getTaskOptimismAgeMs(caseId, taskName) !== null;
}

export function clearTaskOptimism(caseId: string, taskName: string): void {
  removeRecord(storageKey(caseId, taskName));
}

export type ActiveOptimism = { taskName: string; ageMs: number };

export function listActiveOptimism(caseId: string): ActiveOptimism[] {
  const casePrefix = `${KEY_PREFIX}${caseId}:`;
  const keys: string[] = [];

  try {
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(casePrefix)) keys.push(key);
    }
  } catch {
    return [];
  }

  return keys.flatMap((key) => {
    const taskName = key.slice(casePrefix.length);
    const ageMs = getTaskOptimismAgeMs(caseId, taskName);
    return ageMs === null ? [] : [{ taskName, ageMs }];
  });
}
